import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress
} from "@mui/material";
import { useAddBlogMutation, useUpdateBlogMutation } from "../api/blogsApi";
import { Blog } from "../types/blog";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  blog?: Blog | null;
}

const AddEditBlog: React.FC<Props> = ({ open, setModalOpen, onSuccess, blog }) => {
  const [formData, setFormData] = useState<{
    title: string;
    slug: string;
    content: string;
    blog_image: File | null;
    images: File[];
    existingImages: Blog["images"];
    existingBlogImage?: Blog["image"];
  }>({
    title: "",
    slug: "",
    content: "",
    blog_image: null,
    images: [],
    existingImages: [],
    existingBlogImage: undefined,
  });

  const [addBlog] = useAddBlogMutation();
  const [updateBlog] = useUpdateBlogMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (blog) {
      setFormData({
        title: blog.title,
        slug: blog.slug,
        content: blog.content,
        blog_image: null,
        images: [],
        existingImages: blog.images || [],
        existingBlogImage: blog.image,
      });
    } else {
      setFormData({
        title: "",
        slug: "",
        content: "",
        blog_image: null,
        images: [],
        existingImages: [],
        existingBlogImage: undefined,
      });
    }
  }, [blog, open]);

  const handleContentChange = useCallback((value: string) => {
    setFormData(prev => ({ ...prev, content: value }));
  }, []);

  const handleBlogImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, blog_image: file }));
  };

  const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));
  };

  const removeNewImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const removeExistingImage = (id: number) => {
    setFormData((prev) => ({
      ...prev,
      existingImages: prev.existingImages.filter((img) => img.id !== id),
    }));
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    const fd = new FormData();
    fd.append("title", formData.title);
    fd.append("slug", formData.slug);
    fd.append("content", formData.content);
    if (formData.blog_image) {
      fd.append("blog_image", formData.blog_image);
    }
    formData.images.forEach((file, i) => fd.append(`images[${i}]`, file));
    formData.existingImages.forEach((img, i) => {
      if (img.id) {
        fd.append(`existing_image_ids[${i}]`, img.id.toString());
      }
    });

    try {
      if (blog?.id) {
        await updateBlog({ id: blog.id, body: fd }).unwrap();
      } else {
        await addBlog({ body: fd }).unwrap();
      }
      setModalOpen(false);
      onSuccess?.();
      setSubmissionError(null);
    } catch (err) {
      console.error("Error saving blog:", err);
      setSubmissionError("Failed to save blog. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Quill modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  // Quill formats configuration
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link'
  ];

  return (
    <Dialog open={open} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{blog ? "Edit Blog" : "Add Blog"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          label="Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />
        
        <Box sx={{ mt: 2, mb: 2 }}>
          <ReactQuill
            theme="snow"
            value={formData.content}
            onChange={handleContentChange}
            modules={modules}
            formats={formats}
            style={{ height: '200px' }}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" component="label">
            Upload Blog Image
            <input hidden accept="image/*" type="file" onChange={handleBlogImageChange} />
          </Button>
          {formData.existingBlogImage && !formData.blog_image && (
            <Box mt={1}>
              <img src={formData.existingBlogImage} alt="blog" width={100} />
            </Box>
          )}
          {formData.blog_image && (
            <Box mt={1}>
              <img src={URL.createObjectURL(formData.blog_image)} alt="preview" width={100} />
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" component="label">
            Upload Additional Images
            <input hidden multiple accept="image/*" type="file" onChange={handleImagesChange} />
          </Button>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
            {formData.existingImages.map((img) => (
              <Box key={img.id} sx={{ position: "relative" }}>
                <img src={img.url} alt="existing" width={100} height={100} />
                <Button size="small" onClick={() => removeExistingImage(img.id)} sx={{ position: "absolute", top: 0, right: 0 }}>
                  ×
                </Button>
              </Box>
            ))}
            {formData.images.map((file, index) => (
              <Box key={index} sx={{ position: "relative" }}>
                <img src={URL.createObjectURL(file)} alt="preview" width={100} height={100} />
                <Button size="small" onClick={() => removeNewImage(index)} sx={{ position: "absolute", top: 0, right: 0 }}>
                  ×
                </Button>
              </Box>
            ))}
          </Box>
        </Box>

        {submissionError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submissionError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setModalOpen(false)} disabled={isSaving}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditBlog;
